'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { Gift, HandHeart, TrendingUp } from 'lucide-react';

interface Plan {
    name: string;
}

interface WhatIsPlanSectionProps {
    plan: Plan;
}

export function WhatIsPlanSection({ plan }: WhatIsPlanSectionProps) {
    const t = useTranslations('planDetails.helpPlan.whatIsPlan');

    return (
        <SectionWrapper variant="gradient" padding="md" className="relative">
            {/* Decorative background elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
            </div>

            <div className="container relative z-10">
                <div className="max-w-6xl mx-auto">
                    {/* Title Section */}
                    <div className="text-center mb-16">

                        <Typography variant="h2" className="mb-6 text-gray-900 dark:text-white">
                            {t('title')}
                        </Typography>
                    </div>

                    {/* Content Grid */}
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* First Description */}
                        <div className="relative group">
                            <div className="absolute -top-3 -left-3 w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 flex items-center justify-center shadow-lg z-10">
                                <HandHeart className="w-6 h-6 text-white" />
                            </div>
                            <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 pt-12 border-l-4 border-primary-500 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                                <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {t('description1')}
                                </Typography>
                            </div>
                        </div>

                        {/* Second Description */}
                        <div className="relative group">
                            <div className="absolute -top-3 -left-3 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 flex items-center justify-center shadow-lg z-10">
                                <HandHeart className="w-6 h-6 text-white" />
                            </div>
                            <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 pt-12 border-l-4 border-blue-500 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                                <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {t('description2')}
                                </Typography>
                            </div>
                        </div>

                        {/* Third Description */}
                        <div className="relative group">
                            <div className="absolute -top-3 -left-3 w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 flex items-center justify-center shadow-lg z-10">
                                <TrendingUp className="w-6 h-6 text-white" />
                            </div>
                            <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 pt-12 border-l-4 border-green-500 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                                <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {t('description3')}
                                </Typography>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

